import { hasKey, t } from '@app/i18n';
import type { ApiData } from '@app/api/typed';

/**
 * Vocabulário de T25 — «Conversar com agente».
 *
 * A tela dispara uma chamada de tool **de verdade** no lab e mostra por onde ela
 * passou. Até 29/08 isso só saía de dentro do cluster, por `scripts/lab-chamar.sh`.
 */

export type AgentCallResult = ApiData<'/api/v1/agents/{slug}/call', 'post'>;
export type LabPerson = ApiData<'/api/v1/agents/people'>[number];
export type AgentHop = NonNullable<AgentCallResult['hops']>[number];

/**
 * As etapas do caminho, na ordem, com o rótulo que a tela mostra.
 *
 * A trilha responde **duas** perguntas, e só elas: quem chamou quem, e qual foi a
 * decisão de autorização (decisão de 30/08, demo).
 *
 * Ela chegou a desenhar sete linhas, com `sts`, `gateway`, `opa` e `mcp` entre
 * elas. No laboratório o efeito era o contrário do pretendido: o middleware não
 * relata `sts`, `opa` nem `mcp`, então três das quatro saíam «não informado» — e
 * na delegação a do `gateway` saía não-ok, numa chamada que devolveu 200. Quem
 * assiste lê quatro linhas ruins e conclui que o produto falhou, na hora exata em
 * que ele deu certo.
 *
 * O que o executor relata dessas etapas continua chegando no JSON e continua na
 * trilha de auditoria: o que mudou é a tela não desenhar linha para elas (ver
 * `ETAPAS_FORA_DA_TRILHA`).
 *
 * A lista é desenhada **inteira** a cada chamada: etapa que o executor não
 * relatou fica «não informado», e não some. A diferença importa — um salto
 * ausente pode ser "o middleware não conta esse pedaço" ou "a chamada parou
 * antes", e são coisas opostas para quem está depurando o lab.
 */
// O `id` é DADO (chaveia o salto que o executor relata e o tipo `StageID`); o
// rótulo e a descrição são texto, e por isso saem do catálogo na hora de pintar
// — a lista continua `as const` para o tipo, sem frase dentro.
export const STAGES = [
  { id: 'pessoa' },
  { id: 'agente' },
  { id: 'a2a' },
  { id: 'decisao' },
] as const;

/** Rótulo da etapa na trilha. */
export function stageLabel(id: StageID): string {
  return t(`conversation.stage.${id}`);
}

/** O que a etapa fez, em três palavras, sob o rótulo. */
export function stageDescription(id: StageID): string {
  return t(`conversation.stage.${id}.description`);
}

export type StageID = (typeof STAGES)[number]['id'];

/** Etapas de uma chamada direta — `a2a` só existe na delegação. */
export const CALL_STAGES = STAGES.filter((e) => e.id !== 'a2a');

/**
 * O que o executor relata e a trilha **não** desenha (decisão de 30/08, demo).
 *
 * `sts`, `gateway` e `mcp` são etapas de verdade do caminho, e continuam contando
 * para o desfecho da chamada (`desfechoDosSaltos`) — o que elas não são é o que a
 * trilha existe para responder.
 *
 * `decisao` é outra coisa: é o executor avisando que o veredito não vem nesta
 * resposta. Como salto ela ganhava um ✕ vermelho e a palavra «falhou» sobre um
 * passo que não tinha nada a fazer aqui. A linha `decisao` da trilha **não** vem
 * dela — é derivada, com o salto do PDP como fonte quando ele existe.
 */
export const STAGES_OUTSIDE_TRAIL = new Set<string>(['sts', 'gateway', 'mcp', 'decisao']);

/**
 * O que o executor relata e que não é salto do caminho — não entra no desfecho.
 *
 * Só a `decisao`: ela não pode ter dado certo nem errado, porque não havia o que
 * fazer nela. As outras três de `ETAPAS_FORA_DA_TRILHA` saíram da **tela**, não
 * do que aconteceu — lê-las aqui é o que mantém o desfecho da chamada inteiro.
 */
const NOT_AND_HOP = new Set<string>(['decisao']);

/**
 * A etapa em que o executor relata a decisão do PDP — a fonte da linha `decisao`
 * quando ela existe.
 *
 * O mock a manda («ALLOW · a tool está no contrato e no perfil»); o middleware do
 * laboratório não, e aí a linha se deriva do desfecho da chamada. As duas fontes
 * dizem a mesma coisa; a primeira diz melhor, por isso vem antes.
 */
const PDP_STAGE = 'opa';

/**
 * A situação de um salto — o tri-estado de T25 (30/08).
 *
 * `ok` = aconteceu e a chamada seguiu. `negado` = aconteceu e o veredito foi
 * recusar; é **resultado**, não erro, e é o que a demonstração existe para
 * mostrar. `falhou` = não aconteceu: rede, timeout, 5xx, resposta ilegível.
 * `null` = não informado.
 */
export type HopStatus = 'ok' | 'negado' | 'falhou' | null;

/**
 * A situação de um salto relatado.
 *
 * O campo vem pronto do backend, que o deriva do status e do código da chamada.
 * Executor antigo, sem o campo, é lido como antes — `ok`/«não deu certo» —
 * porque sem ele não há como distinguir recusa de defeito, e escolher um dos dois
 * seria adivinhar. É por isso que a derivação mora no backend e não aqui: lá ela
 * tem o `corpo.erro`; aqui, só teria o booleano.
 */
export function hopStatus(hop: AgentHop): HopStatus {
  if (hop.situation) return hop.situation;
  return hop.ok ? 'ok' : 'falhou';
}

/** Um salto pronto para desenhar: o relatado, ou o «não informado». */
export interface HopInScreen {
  id: StageID;
  rotulo: string;
  descricao: string;
  /** `null` = o executor não relatou esta etapa. */
  salto: AgentHop | null;
  /** `null` = etapa não relatada, ou relatada sem situação. */
  situacao: HopStatus;
  /**
   * O texto da linha quando não há salto relatado por trás dela — hoje só a
   * `decisao`, que é derivada. Sem ele a linha diria «não informado» sobre um
   * veredito que a resposta da chamada já conta.
   */
  texto?: string;
  /**
   * A linha aconteceu e só **repassou** a negação decidida adiante.
   *
   * Ela é desenhada como as que deram certo (✓) — porque deram —, mas com a frase
   * dizendo o que fez, e não o detalhe cru do executor. Sem a frase, um ✓ ao lado
   * de «HTTP 403 em …/delegar» leria como sucesso da chamada.
   */
  repassou?: boolean;
}

/**
 * O que a linha `decisao` diz, por desfecho (decisão de 30/08, demo).
 *
 * `negado` não está aqui de propósito: a frase dele sai de `fraseDoSaltoNegado`,
 * que diz também o **porquê**, com as listas que o executor mandou.
 */
/** Chave por desfecho — o desfecho é dado do executor, a frase é da tela. */
function decisionText(desfecho: string): string | undefined {
  const chave = `conversation.decision.${desfecho}`;
  return hasKey(chave) ? t(chave) : undefined;
}

/**
 * Onde a recusa aconteceu — o passo que **decidiu**, e não os que a repassaram.
 *
 * (decisão de 30/08, demo) Medido no laboratório (`f39f699`): a delegação `bruna ·
 * atendimento → cobranca · minhafatura.consultar` toma o `403` que o PDP tem de
 * dar, e a trilha pintava ⊘ no Agente e no A2A. Quem assiste lê «o atendimento
 * falhou» sobre um agente que só devolveu a resposta que recebeu — e sobre uma
 * delegação que aconteceu.
 *
 * O vocabulário é o mesmo do backend (`codigosDeRecusa`, em `situacao.go`):
 *
 *   agente_fora_do_contrato        → a **delegação**: o alvo não está em «Pode
 *                                    invocar» nem pela imagem nem pelo contrato; o
 *                                    próprio agente recusa e nada chega ao PDP.
 *   authorization_denied           → o PDP decidiu. **Pode ser as duas coisas**
 *                                    desde o DEF-29 (09/09): a tool, depois do A2A,
 *                                    ou a própria delegação — que agora é
 *                                    capability e passa pelos mesmos dois eixos.
 *   tool_fora_do_escopo_do_agente  → a **tool**: quem recusa é o escopo declarado,
 *                                    e o A2A já tinha acontecido.
 *   (sem código, 401/403)          → a tool, pela mesma regra que o desfecho usa.
 *
 * # COM O MESMO CÓDIGO, COMO SE SABE QUAL DOS DOIS (DEF-29)
 *
 *   Pelo **caminho**, não pelo código: a delegação negada pelo PDP encerra a
 *   chamada ali, e o `a2a` fica sendo o último passo relatado. A tool negada
 *   acontece depois — o executor relata `gateway`/`opa`/`mcp` adiante, ou (no lab)
 *   nem chega a relatar o `a2a`.
 *
 *   A situação do salto `a2a` sozinha não serve, e a razão é a de 30/08: contra o
 *   middleware do laboratório todo salto que viu o 403 passar volta não-ok, e o
 *   `a2a` de uma tool negada ficaria ⊘ — três passos «errados» numa chamada em que
 *   houve um veredito só. «Último passo do caminho» separa os dois casos sem
 *   depender de quem marcou o salto.
 */
function whereRefused(refusal: ErrorBody, hops: AgentHop[] | undefined): StageID {
  if (refusal.erro === 'agente_fora_do_contrato') return 'a2a';
  const last = lastPathStep(hops);
  if (last && last.step === 'a2a' && hopStatus(last) !== 'ok') return 'a2a';
  return 'decisao';
}

/**
 * O último passo do **caminho** que o executor relatou.
 *
 * `decisao` fica de fora: ela não é passo, é o aviso de que o veredito vem pelo
 * log (ver `NOT_AND_HOP`). Contá-la aqui faria toda chamada terminar nela.
 */
function lastPathStep(hops: AgentHop[] | undefined): AgentHop | null {
  const path = (hops ?? []).filter((hop) => !NOT_AND_HOP.has(hop.step));
  return path.length ? path[path.length - 1] : null;
}

/** A situação de uma linha, já sabendo onde a recusa aconteceu. */
interface StatusInRefusal {
  situacao: HopStatus;
  repassou: boolean;
}

/**
 * Reinterpreta a situação de uma linha à luz de **onde** a recusa aconteceu.
 *
 * O executor marca como não-ok todo salto que viu um erro passar por ele, e o
 * backend traduz isso para `negado` quando a chamada foi recusada — está certo do
 * ponto de vista dele. Para quem lê a trilha, porém, «negado» tem de ser o passo
 * que **decidiu**; os anteriores repassaram, e pintá-los de âmbar conta uma
 * história em que três coisas deram errado.
 *
 * Duas exceções, e as duas importam:
 *
 *  - `decisao` nunca é reinterpretada: ela **é** o veredito da chamada, e numa
 *    chamada recusada é ⊘ mesmo quando quem decidiu foi o A2A.
 *  - `falhou` do executor manda: um passo que quebrou não vira ✓ porque houve uma
 *    recusa adiante. Quebra e recusa são coisas diferentes, e é a distinção que a
 *    trilha inteira existe para mostrar.
 */
function statusInRefusal(
  etapa: StageID,
  base: HopStatus,
  whereWas: StageID | null,
  repassador: boolean,
): StatusInRefusal {
  if (whereWas === null || etapa === 'decisao') return { situacao: base, repassou: false };
  if (etapa === whereWas) return { situacao: 'negado', repassou: false };
  if (base === 'falhou') return { situacao: 'falhou', repassou: false };
  // Só o agente ganha a frase: o que a linha `a2a` fez já está no texto dela
  // («delegou a cobranca»), e repeti-lo como «repassou» apagaria o slug.
  if (base === 'negado') return { situacao: 'ok', repassou: etapa === 'agente' && repassador };
  return { situacao: base, repassou: false };
}

/**
 * O agente **repassou** uma negação, ou foi ele quem recusou?
 *
 * Quase sempre a primeira: o PDP decide depois dele, e o que ele devolve é a
 * resposta que recebeu. A exceção é `tool_fora_do_escopo_do_agente` numa chamada
 * **direta** — aí quem recusa é o próprio agente, porque a tool não está no
 * escopo que ele declara, e não há nada além dele de onde a negação viesse.
 * Escrever «repassou a negação» ali seria dizer na tela uma coisa que não
 * aconteceu. Com delegação o código volta a significar repasse: quem não declara
 * a tool é o especialista, e o generalista devolve o que ele respondeu.
 *
 * A situação da linha não muda com isto — só quem decidiu fica ⊘, como a regra de
 * 30/08 manda. O que muda é a **frase**: sem repasse, a linha mostra o detalhe do
 * próprio executor, que já diz o que houve («cobranca não declara …»).
 */
function agentRepassou(refusal: ErrorBody, delegation: boolean): boolean {
  return refusal.erro !== 'tool_fora_do_escopo_do_agente' || delegation;
}

/** O que `trilhaNaTela` precisa saber da chamada. */
export interface TrailOptions {
  saltos: AgentHop[] | undefined;
  /** `true` desenha a etapa `a2a` — só a delegação a atravessa. */
  delegacao: boolean;
  /** O desfecho da chamada inteira (`situacaoDaTroca`); `null` = ainda em voo. */
  desfecho?: HopStatus;
  /** Slug do especialista: o texto da linha `a2a` quando ela é derivada. */
  delegadoA?: string | null;
  /** O corpo da recusa: é dele que sai **onde** a recusa aconteceu. */
  recusa?: ErrorBody;
}

/**
 * Cruza a lista canônica com o que voltou. Uma etapa relatada fora da lista
 * (executor mais novo que esta tela) entra no fim em vez de sumir — quem lê
 * prefere ver um nome desconhecido a não ver o salto. As de
 * `ETAPAS_FORA_DA_TRILHA` são a exceção: elas não desenham linha.
 *
 * `desfecho` é o que aconteceu com a chamada **inteira** (`situacaoDaTroca`) e
 * alimenta as **duas linhas derivadas**, `a2a` e `decisao`, quando o executor não
 * relata o salto correspondente. `recusa` diz **onde** a negação aconteceu — ver
 * `ondeRecusou`.
 *
 * Um objeto, e não parâmetros posicionais: `desfecho` (`'ok'|'negado'|…`) e
 * `delegadoA` (`string`) são compatíveis entre si, e trocá-los de lugar
 * compilaria.
 */
export function trailInScreen(opcoes: TrailOptions): HopInScreen[] {
  const { saltos, delegacao, desfecho = null, delegadoA = null, recusa } = opcoes;
  const whereWas = desfecho === 'negado' ? whereRefused(recusa ?? {}, saltos) : null;
  const repassador = agentRepassou(recusa ?? {}, delegacao);
  const relatados = new Map(
    (saltos ?? []).filter((s) => !STAGES_OUTSIDE_TRAIL.has(s.step)).map((s) => [s.step, s]),
  );
  // O salto do PDP não ganha linha própria: ele **é** a linha `decisao`. Tirá-lo
  // do mapa é o que impede o laço das desconhecidas, no fim, de desenhá-lo de
  // novo — uma vez como `decisao`, outra como «opa».
  const doPDP = relatados.get(PDP_STAGE) ?? null;
  relatados.delete(PDP_STAGE);

  const canonical = delegacao ? STAGES : CALL_STAGES;

  const linhas: HopInScreen[] = canonical.map((etapa) => {
    if (etapa.id === 'decisao') {
      return decisionRow(stageLabel(etapa.id), stageDescription(etapa.id), doPDP, desfecho);
    }
    if (etapa.id === 'a2a') {
      return delegationRow(
        stageLabel(etapa.id),
        stageDescription(etapa.id),
        relatados.get('a2a') ?? null,
        desfecho,
        delegadoA,
        whereWas,
      );
    }
    const hop = relatados.get(etapa.id) ?? null;
    const { situacao, repassou } = statusInRefusal(
      etapa.id,
      hop ? hopStatus(hop) : null,
      whereWas,
      repassador,
    );
    return {
      id: etapa.id,
      rotulo: stageLabel(etapa.id),
      descricao: stageDescription(etapa.id),
      salto: hop,
      situacao,
      repassou,
    };
  });

  const conhecidas = new Set<string>(canonical.map((e) => e.id));
  for (const hop of relatados.values()) {
    if (!conhecidas.has(hop.step)) {
      linhas.push({
        id: hop.step as StageID,
        rotulo: hop.step,
        descricao: 'etapa relatada pelo executor e desconhecida desta tela',
        salto: hop,
        situacao: hopStatus(hop),
      });
    }
  }
  return linhas;
}

/**
 * A linha `decisao`: o veredito de autorização, e nada de transporte.
 *
 * Duas fontes, nesta ordem: o salto do PDP quando o executor o relata — ele já
 * vem legível («ALLOW · …») — e o desfecho da chamada quando não, que é o caso do
 * laboratório. Nunca o salto `decisao` do executor: aquele é só o aviso de que o
 * veredito chega pelo log, e era ele que desenhava «✕ falhou» sobre um 200.
 */
function decisionRow(
  rotulo: string,
  descricao: string,
  doPDP: AgentHop | null,
  outcome: HopStatus,
): HopInScreen {
  const situacao = doPDP?.situation ? hopStatus(doPDP) : outcome;
  return {
    id: 'decisao',
    rotulo,
    descricao,
    salto: doPDP,
    situacao,
    texto: decisionText(situacao ?? 'aguardando'),
  };
}

/**
 * A linha `a2a`: quem delegou para quem.
 *
 * Mesma forma da linha da decisão, pela mesma razão (medido no laboratório em
 * 30/08, build `dea7ea3`): numa delegação de 200 o middleware **não relata** o
 * salto `a2a`, e a linha canônica saía «– não informado» ao lado de três linhas
 * boas. Não é o que aconteceu — a delegação aconteceu, e o cabeçalho da troca já
 * a mostra («bruna → atendimento → cobranca»).
 *
 * Duas fontes, nesta ordem: o salto relatado, quando existe (contra o mock ele
 * existe e é legível — «“cobranca” está em “Pode invocar” de atendimento»); e o
 * desfecho da chamada quando não. Sem tempo: ninguém mediu esta etapa, e um `0`
 * na tela afirmaria uma medição que não houve.
 *
 * Sem o slug do especialista o texto cai na descrição da etapa — genérico, mas
 * verdadeiro; inventar um nome seria pior do que não o dizer.
 */
function delegationRow(
  rotulo: string,
  descricao: string,
  relatado: AgentHop | null,
  outcome: HopStatus,
  delegated: string | null,
  whereWas: StageID | null,
): HopInScreen {
  const base = relatado?.situation ? hopStatus(relatado) : outcome;
  const { situacao, repassou } = statusInRefusal('a2a', base, whereWas, false);
  return {
    id: 'a2a',
    rotulo,
    descricao,
    salto: relatado,
    situacao,
    repassou,
    texto: delegated ? t('conversation.stage.a2a.delegated', { agent: delegated }) : descricao,
  };
}

/**
 * Códigos que o agente e o gateway devolvem em `corpo.erro`. São o vocabulário
 * **da chamada**, não o `code` do envelope: quem os traduz é esta tela.
 */
export const CALL_ERROR_CODES = [
  'authorization_denied',
  'tool_fora_do_escopo_do_agente',
  'agente_fora_do_contrato',
] as const;

/** A frase de um código de erro da chamada, ou `undefined` se não o conhecemos. */
export function callError(codigo: string): string | undefined {
  const chave = `conversation.callError.${codigo}`;
  return hasKey(chave) ? t(chave) : undefined;
}

/** Corpo de uma chamada recusada, na forma que o executor devolve. */
export interface ErrorBody {
  erro?: string;
  mensagem?: string;
  /** Tools que o contrato do workload concede — a metade esquerda do cruzamento. */
  workload_pode?: string[];
  /** Tools que o perfil da pessoa carrega — a metade direita. */
  perfil_pode?: string[];
  /** Agentes em «Pode invocar», quando a recusa é de delegação. */
  pode_invocar?: string[];
  /** Tools que o agente declara, quando a tool está fora do escopo dele. */
  tools_do_agente?: string[];
}

/** Corpo de uma chamada bem-sucedida. */
export interface SuccessBody {
  executado?: string;
  gateway_status?: number;
}

/** Frase de fallback de um salto negado sem código que a tela conheça. */
export const refusalWithoutCode = (): string => t('conversation.refusal.withoutCode');

/**
 * A frase de um salto **negado**: quem recusou e por quê.
 *
 * Ela existe porque o detalhe que o middleware manda nesses saltos é jargão de
 * transporte — «HTTP 403», «HTTP 403 no gateway (APIM), segundo o agente» —, e
 * quem assiste à demonstração não lê 403 como «o produto barrou uma chamada que
 * não devia passar». O detalhe do executor não se perde: ele continua na tela,
 * como informação secundária.
 *
 * Nada aqui é adivinhado. O **quem** vem da etapa; o **porquê**, do `corpo.erro`
 * e das duas listas do cruzamento que o próprio executor mandou — as mesmas que a
 * bolha do agente já mostra. Sem as listas, a frase para em «o PDP não autorizou»
 * em vez de escolher uma metade.
 */
export function hopDeniedPhrase(
  etapa: string,
  refusal: ErrorBody,
  tool: string,
  agente?: string | null,
  delegated?: string | null,
): string {
  return t('conversation.refusal.phrase', {
    verb: refusalVerbo(etapa, refusal.erro),
    reason: refusalReason(etapa, refusal, tool, agente, delegated),
  });
}

/**
 * Quem fez o quê neste salto.
 *
 * Num DENY do PDP quem decide é o OPA e quem aplica é o gateway — os dois
 * **negaram**. O agente, antes deles, só devolveu a resposta que recebeu: ele
 * repassou. Nas outras duas recusas quem recusa é o próprio salto (o agente que
 * não expõe a tool, o generalista sem o alvo em «Pode invocar»).
 */
function refusalVerbo(etapa: string, codigo: string | undefined): string {
  // A linha `decisao` **é** o veredito: quem a lê quer o verbo dele, qualquer que
  // tenha sido o código que o executor devolveu (decisão de 30/08, demo).
  if (etapa === 'decisao') return 'negou';
  if (codigo === 'authorization_denied') {
    // `a2a` entra na lista desde o DEF-29: quando é a delegação que o PDP nega, o
    // passo A2A é onde o veredito aconteceu — não um passo que repassou o de
    // outro. «Repassou a negação» ali diria que a delegação passou.
    return t(
      etapa === 'opa' || etapa === 'gateway' || etapa === 'a2a'
        ? 'conversation.refusal.verb.denied'
        : 'conversation.refusal.verb.relayed',
    );
  }
  return t('conversation.refusal.verb.refused');
}

function refusalReason(
  etapa: string,
  refusal: ErrorBody,
  tool: string,
  agente?: string | null,
  delegated?: string | null,
): string {
  switch (refusal.erro) {
    case 'tool_fora_do_escopo_do_agente':
      return t('conversation.refusal.toolOutOfScope');
    case 'agente_fora_do_contrato':
      // Com os dois slugs a frase diz quem e quem; sem eles, a genérica, que é
      // verdadeira. Nomear é melhor do que descrever, e inventar é pior que ambos.
      return agente && delegated
        ? t('conversation.refusal.agentOutOfContract.named', { agent: agente, delegated })
        : t('conversation.refusal.agentOutOfContract');
    case 'authorization_denied': {
      // As duas listas dizem qual metade do cruzamento ficou vazia. Lista
      // ausente é «não sei», e não «está lá».
      //
      // DEF-29: na delegação as MESMAS duas chaves trazem **agentes**, e o alvo do
      // cruzamento é o especialista — não a tool. Cruzar a tool com listas de
      // agentes daria sempre «não está em nenhuma das duas»: verdade sobre a
      // pergunta errada, que é a pior espécie de frase numa tela de autorização.
      const delegation = etapa === 'a2a';
      const alvo = delegation ? (delegated ?? '') : tool;
      const outsideProfile = refusal.perfil_pode ? !refusal.perfil_pode.includes(alvo) : false;
      const outsideContract = refusal.workload_pode ? !refusal.workload_pode.includes(alvo) : false;

      if (delegation) {
        const ofAgent = agente
          ? t('conversation.refusal.delegation.ofAgent', { agent: agente })
          : t('conversation.refusal.delegation.ofCaller');
        const toTarget = delegated
          ? t('conversation.refusal.delegation.toTarget', { delegated })
          : t('conversation.refusal.delegation.asked');
        if (outsideProfile && outsideContract) {
          return t('conversation.refusal.delegation.both', { ofAgent, toTarget });
        }
        if (outsideProfile) {
          return t('conversation.refusal.delegation.profile', { toTarget });
        }
        if (outsideContract) {
          return t('conversation.refusal.delegation.contract', { ofAgent, toTarget });
        }
        return t('conversation.refusal.delegation.pdp');
      }

      if (outsideProfile && outsideContract) {
        return t('conversation.refusal.call.both');
      }
      if (outsideProfile) return t('conversation.refusal.call.profile');
      if (outsideContract) return t('conversation.refusal.call.contract');
      return t('conversation.refusal.call.pdp');
    }
    default:
      return refusal.mensagem ?? refusalWithoutCode();
  }
}

export function callDeuRight(resultado: AgentCallResult): boolean {
  return resultado.http_status >= 200 && resultado.http_status <= 299;
}

export function errorBody(resultado: AgentCallResult): ErrorBody {
  return (resultado.body ?? {}) as ErrorBody;
}

export function successBody(resultado: AgentCallResult): SuccessBody {
  return (resultado.body ?? {}) as SuccessBody;
}

/**
 * A frase da resposta do agente. Erro sem código conhecido cai na mensagem que o
 * executor mandou — nunca numa frase genérica que apague o que ele disse.
 */
export function agentResponse(resultado: AgentCallResult): string {
  if (callDeuRight(resultado)) {
    const corpo = successBody(resultado);
    return t('conversation.swap.executed', { tool: corpo.executado ?? resultado.tool });
  }
  const corpo = errorBody(resultado);
  const traduzido = corpo.erro ? callError(corpo.erro) : undefined;
  return (
    traduzido ??
    corpo.mensagem ??
    phraseWithoutBody(hopsOutcome(resultado.hops), resultado.http_status)
  );
}

/**
 * O desfecho da chamada lido **dos saltos**: `negado` quando algum salto foi
 * negado, `falhou` quando algum falhou.
 *
 * A regra que separa recusa de defeito é do backend (ele tem o `corpo.erro` e o
 * status). Reescrevê-la aqui daria duas versões da mesma conta, e o dia em que
 * uma mudasse a tela e a trilha de T23 discordariam sobre a mesma chamada. Então
 * a tela **lê o resultado** dela, que chega em cada salto.
 */
export function hopsOutcome(saltos: AgentHop[] | undefined): HopStatus {
  // `NAO_E_SALTO`, e não `ETAPAS_FORA_DA_TRILHA`: `sts`, `gateway` e `mcp`
  // deixaram de ser desenhados (30/08), mas continuam sendo o que aconteceu —
  // ignorá-los aqui apagaria o «negado» do gateway, que é a fonte do tom da
  // bolha numa recusa do lab.
  const statuses = (saltos ?? []).filter((s) => !NOT_AND_HOP.has(s.step)).map(hopStatus);
  if (statuses.includes('negado')) return 'negado';
  if (statuses.includes('falhou')) return 'falhou';
  return null;
}

/**
 * A frase quando o executor não mandou corpo nenhum — o caso do lab.
 *
 * Numa negação ela diz «negado pelo PDP»: era aqui que a tela dizia «a chamada
 * foi recusada (HTTP 403)», e o 403 na frase principal fazia a demonstração
 * parecer defeito. O status continua na tela, como detalhe secundário da bolha.
 */
function phraseWithoutBody(outcome: HopStatus, status: number): string {
  if (outcome === 'negado') return t('conversation.decision.deniedByPdp');
  if (outcome === 'falhou') return t('conversation.decision.incomplete');
  return t('conversation.decision.notConcluded', { status });
}

// --- a troca na tela ---------------------------------------------------------
//
// O modelo é **um só** para os dois transportes. Pelo canal a troca nasce vazia e
// cresce (saltos, depois resposta, depois decisão); pelo REST ela nasce quase
// pronta. Um modelo por transporte daria dois renderizadores para a mesma cena, e
// o dia em que a resposta do LLM vier em pedaços só um deles saberia crescer.

/** O que o chamador recebeu do agente. `null` enquanto a resposta não chegou. */
export interface CallResponse {
  statusHttp: number;
  body: unknown;
  requestId: string;
  duracaoMs: number;
}

/**
 * O que a linha «Decisão do PDP» mostra.
 *
 * Um tipo só para os dois transportes: pelo canal o estado vem do evento
 * `decisao`, pelo REST do polling de `listDecisions`. `nao_encontrada` **não** é
 * um veredito do PDP — é o prazo ter esgotado —, e a tela precisa distingui-lo de
 * um DENY exatamente como distingue «não informado» de «falhou» num salto.
 *
 * Não há campo de link: `/decisoes` é rota única e a linha certa se abre por
 * estado de rota, com o `requestId` — que é o **do log do gateway**, não o da
 * chamada.
 */
export type DecisionState =
  | { estado: 'aguardando' }
  | { estado: 'encontrada'; requestId: string; veredito: string }
  | { estado: 'nao_encontrada' };

export interface SwapInScreen {
  /** Chave estável do envio; identifica a troca antes de haver `request_id`. */
  chave: string;
  pessoa: string;
  agente: string;
  /** Slug do especialista, quando houve delegação. */
  delegadoA: string | null;
  tool: string;
  saltos: AgentHop[];
  resposta: CallResponse | null;
  decisao: DecisionState;
  /** UUID de quem executou — para o «Reproduzir no simulador». */
  identityId: string | null;
  /** Instante do envio (RFC 3339): a decisão procurada é posterior a ele. */
  desde: string;
}

/** Quem executou a tool: o delegado, quando houve delegação. */
export function swapExecutor(swap: SwapInScreen): string {
  return swap.delegadoA ?? swap.agente;
}

export function swapDeuRight(swap: SwapInScreen): boolean {
  const status = swap.resposta?.statusHttp ?? 0;
  return status >= 200 && status <= 299;
}

export function swapErrorBody(swap: SwapInScreen): ErrorBody {
  return (swap.resposta?.body ?? {}) as ErrorBody;
}

/**
 * O desfecho da troca: `ok`, `negado`, `falhou` — ou `null` enquanto a chamada
 * está em voo (e quando os saltos não bastam para dizer).
 *
 * É o que decide o tom da bolha e a palavra que ela usa. Vem dos saltos, não de
 * uma segunda conta feita aqui — ver `desfechoDosSaltos`.
 */
export function swapStatus(swap: SwapInScreen): HopStatus {
  if (!swap.resposta) return null;
  if (swapDeuRight(swap)) return 'ok';
  return hopsOutcome(swap.saltos) ?? statusOutcome(swap);
}

/**
 * O desfecho lido do status e do código do corpo — a segunda fonte, para quando
 * nenhum salto relatado diz o que aconteceu (decisão de 30/08, demo).
 *
 * É a mesma regra de `desfechoDaChamada` no backend
 * (`usecases/agentes/situacao.go`), e a duplicação é consciente: a linha
 * `decisao` da trilha **precisa** de um veredito, e no laboratório a resposta do
 * 403 chega sem corpo e com saltos que não o carimbam. Sem esta conta a linha
 * ficaria «não concluída» sobre uma recusa — que é o oposto do que aconteceu.
 * Quando o salto vem carimbado, ele ganha: a regra de cima é a do backend.
 */
function statusOutcome(swap: SwapInScreen): HopStatus {
  const status = swap.resposta?.statusHttp ?? 0;
  if (status >= 200 && status <= 299) return 'ok';
  const codigo = swapErrorBody(swap).erro;
  if (codigo && CALL_ERROR_CODES.includes(codigo as (typeof CALL_ERROR_CODES)[number]))
    return 'negado';
  if (status === 401 || status === 403) return 'negado';
  return 'falhou';
}

/**
 * A frase da resposta do agente. Erro sem código conhecido cai na mensagem que o
 * executor mandou — nunca numa frase genérica que apague o que ele disse. Sem
 * corpo nenhum, quem responde é o desfecho dos saltos: numa negação a bolha diz
 * «negado pelo PDP», e o HTTP fica como detalhe secundário.
 */
export function swapResponse(swap: SwapInScreen): string {
  if (!swap.resposta) return t('conversation.swap.calling');
  if (swapDeuRight(swap)) {
    const corpo = (swap.resposta.body ?? {}) as SuccessBody;
    return t('conversation.swap.executed', { tool: corpo.executado ?? swap.tool });
  }
  const corpo = swapErrorBody(swap);
  const traduzido = corpo.erro ? callError(corpo.erro) : undefined;
  return (
    traduzido ?? corpo.mensagem ?? phraseWithoutBody(swapStatus(swap), swap.resposta.statusHttp)
  );
}

/** Monta a troca a partir do resultado do REST, que chega inteiro de uma vez. */
export function resultRestSwap(opcoes: {
  chave: string;
  resultado: AgentCallResult;
  identityId: string | null;
  desde: string;
}): SwapInScreen {
  const { chave, resultado, identityId, desde } = opcoes;
  return {
    chave,
    pessoa: resultado.person,
    agente: resultado.agent,
    delegadoA: resultado.delegated_to ?? null,
    tool: resultado.tool,
    saltos: resultado.hops ?? [],
    resposta: {
      statusHttp: resultado.http_status,
      body: resultado.body ?? null,
      requestId: resultado.request_id,
      duracaoMs: resultado.duration_ms,
    },
    decisao: { estado: 'aguardando' },
    identityId,
    desde,
  };
}

/** Uma tool oferecida no combobox. */
export interface ToolOferecida {
  nome: string;
  /** `false` = está no contrato vigente; `true` = fora dele, e vai dar DENY. */
  foraDoContrato: boolean;
}

/**
 * Monta a lista de tools da tela: as do contrato vigente **mais** as que a
 * imagem declara e o PDP concede sem contrato.
 *
 * As de fora do contrato entram de propósito, marcadas: é com elas que se
 * provoca um DENY na demonstração, e esconder-las deixaria a tela capaz de
 * mostrar só metade da história.
 */
export function toolsOferecidas(capabilities: {
  aprovadas?: { tool_name?: string }[];
  pedidas?: { capabilities?: { tool_name?: string }[] } | null;
  pdp?: { granted_in_pdp?: string[] } | null;
}): ToolOferecida[] {
  const inContract = new Set<string>();
  for (const c of capabilities.aprovadas ?? []) {
    if (c.tool_name) inContract.add(c.tool_name);
  }

  const fora = new Set<string>();
  for (const c of capabilities.pedidas?.capabilities ?? []) {
    if (c.tool_name && !inContract.has(c.tool_name)) fora.add(c.tool_name);
  }
  for (const nome of capabilities.pdp?.granted_in_pdp ?? []) {
    if (!inContract.has(nome)) fora.add(nome);
  }

  return [
    ...[...inContract].sort().map((nome) => ({ nome, foraDoContrato: false })),
    ...[...fora].sort().map((nome) => ({ nome, foraDoContrato: true })),
  ];
}
